import { QText, useToastProvider } from '@qualio/ui-components';
import React, { SyntheticEvent, useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '../../containers/confirmation-modal';
import { Group, ModalComponentProps } from '../../types';
import { createSuccessToast, createErrorToast } from '../../utils/toast.utils';
import { deleteGroup } from '../../api/medtech-api';

type DeleteGroupModalProps = ModalComponentProps & {
  group: Group;
  setRefetch: () => Promise<void>;
};

export const DeleteGroupModal: React.FC<DeleteGroupModalProps> = ({ closeModal, group, setRefetch }) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const [isDeletingGroup, setDeletingGroup] = useState(false);
  const handleDeleteGroup = async (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeletingGroup(true);
    try {
      await deleteGroup(group.id);
      showToast(createSuccessToast(t('messages.success.groupDeleted'), 'group-delete-success-toast'));
      setRefetch();
      closeModal();
    } catch (error) {
      setDeletingGroup(false);
      const axiosError = error as AxiosError;

      const defaultErrorMessage = t('messages.error.deleteGroup');
      if (axiosError.response && axiosError.response.status === 409) {
        const responseData = axiosError.response.data as { error: string };
        const message = responseData.error || defaultErrorMessage;
        showToast(createErrorToast(message, 'group-delete-error-toast'));
      } else {
        showToast(createErrorToast(defaultErrorMessage, 'group-delete-error-toast'));
      }
    }
  };
  return (
    <ConfirmationModal
      closeModal={closeModal}
      title={t('groups.confirmDeleteTitle')}
      cancelLabel={t('common.no')}
      buttonProps={{
        isLoading: isDeletingGroup,
        onClick: handleDeleteGroup,
        label: t('groups.yesDeleteGroup'),
        isDestructive: true,
      }}
    >
      <QText>{t('groups.confirmDeleteMessage', { groupName: group.name })}</QText>
    </ConfirmationModal>
  );
};
